import { useState, type FormEvent } from "react";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/dahfr4roh653ivfvntmg";

const topicOptions = ["二创解读投稿", "话题线索", "活动与合作", "问题反馈"];

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("投稿已收到，编辑部会在三个工作日内查看。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessage("投稿已收到，编辑部会在三个工作日内查看。");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          isSpam
            ? "提交未通过校验，请检查内容后重试。"
            : serverMsg || "提交失败，请稍后再试。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络异常，提交失败，请稍后再试。");
    }
  };

  return (
    <section id="contact" className="w-full scroll-mt-24 bg-background-100/60 py-14 md:py-20" aria-labelledby="contact-title">
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-10 px-4 md:px-8 lg:grid-cols-[1fr_1.1fr] lg:gap-16">
        <div>
          <span className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-secondary-700">
            <i className="ri-send-plane-line text-sm" aria-hidden="true" />
            SUBMIT · 投稿与联系
          </span>
          <h2 id="contact-title" className="mt-3 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
            有料要爆？有话要说？
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            不管是话题线索、二创解读投稿，还是对社区规则的建议，都欢迎写给我们。
            请务必基于公开信息，不要提交任何个人隐私内容。
          </p>

          <ul className="mt-8 space-y-4">
            {[
              { icon: "ri-time-line", text: "编辑部会在三个工作日内查看投稿" },
              { icon: "ri-lock-line", text: "涉及隐私的内容我们会直接处理，不会公开" },
              { icon: "ri-award-line", text: "优质二创解读会署名发布在解读栏目" },
            ].map((item) => (
              <li key={item.text} className="flex items-start gap-3 text-sm text-foreground-700">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className={`${item.icon} text-lg`} aria-hidden="true" />
                </span>
                {item.text}
              </li>
            ))}
          </ul>
        </div>

        <form
          id="guatian-submit-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <label htmlFor="nickname" className="text-sm font-bold text-foreground-800">
                你的昵称
              </label>
              <input
                id="nickname"
                name="nickname"
                type="text"
                required
                placeholder="例如：瓜田值班员"
                className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
            <div className="flex flex-col gap-2">
              <label htmlFor="email" className="text-sm font-bold text-foreground-800">
                邮箱
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="you@example.com"
                className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
              />
            </div>
          </div>

          <div className="mt-5 flex flex-col gap-2">
            <label htmlFor="topic_type" className="text-sm font-bold text-foreground-800">
              投稿类型
            </label>
            <select
              id="topic_type"
              name="topic_type"
              required
              defaultValue=""
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            >
              <option value="" disabled>
                请选择一个类型
              </option>
              {topicOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-5 flex flex-col gap-2">
            <label htmlFor="content" className="text-sm font-bold text-foreground-800">
              内容详情
            </label>
            <textarea
              id="content"
              name="content"
              required
              maxLength={500}
              rows={5}
              placeholder="说说你想投稿的话题线索或解读思路（最多 500 字）"
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <input
            type="text"
            name="website_alt"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
            className="garden-notes-field"
          />

          <button
            type="submit"
            disabled={status === "submitting"}
            className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className={status === "submitting" ? "ri-loader-4-line animate-spin text-lg" : "ri-send-plane-line text-lg"} aria-hidden="true" />
            {status === "submitting" ? "提交中…" : "提交投稿"}
          </button>

          {status === "success" && (
            <p className="mt-4 flex items-center gap-2 rounded-md bg-secondary-50 px-3.5 py-3 text-sm font-medium text-secondary-800" role="status">
              <i className="ri-checkbox-circle-line text-lg" aria-hidden="true" />
              {message}
            </p>
          )}
          {status === "error" && (
            <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-50 px-3.5 py-3 text-sm font-medium text-primary-700" role="alert">
              <i className="ri-error-warning-line text-lg" aria-hidden="true" />
              {message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}